import * as Lodash from 'lodash/fp'

import {
  FormChangeHandler,
  emptyFormGuidelines,
} from '../../interfaces/components/dynamic-form'

import { resultError, resultSuccess } from '../../interfaces/base'
import {
  InitializeFormatsetForm,
  InitializeCreativeForm,
  IBlueprintService,
  GetCreativeGuidelines,
} from '../../interfaces/services/blueprints'
import { appErrorFromAnything } from '../../utils/error'

import BlueprintChangeHandler from '../../lib/dynamic-form/ChangeHandler'
import { initialize } from '../../lib/dynamic-form/helpers'
import adapters from './adapters'
import { doHttp, decode } from '../../utils/json-api'

const blueprintService: (DI: any) => IBlueprintService = (DI) => {
  const initFormatsetForm: InitializeFormatsetForm = async ({
    type = '',
    projectId,
    id,
    name,
  }) => {
    try {
      const originalBlueprint = await getFormatsetBlueprint(type, id)

      const changeHandler: FormChangeHandler = BlueprintChangeHandler(
        originalBlueprint,
        (value) => ({
          method: 'post',
          url: 'api/formatsets?validate=true',
          data: {
            name,
            project_id: projectId,
            type,
            formatset: value,
          },
        }),
        'formatset',
      )

      let data = initialize(originalBlueprint)
      if (id) {
        const formatsetResponse = await doHttp({
          method: 'get',
          url: `api/formatsets/${id}`,
        })

        const formatsetResponseAdapted = decode(
          adapters.formatset,
          formatsetResponse,
        )

        data = {
          creative_formats: formatsetResponseAdapted.creativeFormats,
          ...formatsetResponseAdapted.data,
        }
      }

      const initial = await changeHandler(originalBlueprint, data)

      return resultSuccess({
        changeHandler,
        value: data,
        blueprint: initial.blueprint,
        guidelines: emptyFormGuidelines(),
        validation: initial.validation,
      })
    } catch (e) {
      console.error(e)
      return resultError(appErrorFromAnything(e))
    }
  }

  const initCreativeForm: InitializeCreativeForm = async (params) => {
    const formatsetId = params.formatsetId
    const formatsetType = params.type
    const creativeId = params.creativeId

    const response = await doHttp({
      method: 'get',
      url: `/api/creatives/${creativeId}/blueprint`,
    })

    const originalBlueprint = decode(adapters.blueprint, response, 'default', {
      name: `${formatsetType}-creative`,
    })

    const changeHandler: FormChangeHandler = BlueprintChangeHandler(
      originalBlueprint,
      (value) => ({
        method: 'put',
        url: `api/creatives/${creativeId}/finalize?validate=true`,
        data: {
          formatset_id: formatsetId,
          creative_type: formatsetType,
          creative: value,
        },
      }),
      'creative',
    )

    const creativeResponse = await doHttp({
      method: 'get',
      url: `api/creatives/${creativeId}?includes=formatset.project`,
    })

    const guidelinesResponse = await getCreativeGuidelines(creativeId)
    const guidelines =
      guidelinesResponse.type === 'error'
        ? emptyFormGuidelines()
        : guidelinesResponse.payload
    const creative = decode(adapters.creative, creativeResponse, 'withProject')

    const initial = await changeHandler(
      originalBlueprint,
      Lodash.merge(initialize(originalBlueprint), creative.data),
    )

    // NTI: since there are to many layers of abstraction that don't do nothing
    // I'm hardcoding video_subtitle field for carousel
    const initialValueForCarousel = {
      ...initial.value,
      creatives: initial.value.creatives?.map((c: any) =>
        Object.assign({ video_subtitle: null }, c),
      ),
    }
    const initialValue =
      creative.type?.name === 'carousel'
        ? initialValueForCarousel
        : initial.value

    return resultSuccess({
      changeHandler,
      value: Lodash.merge(creative.data, initialValue),
      blueprint: initial.blueprint,
      validation: initial.validation,
      guidelines,
    })
  }

  const getFormatsetBlueprint = async (type: string, id?: string) => {
    const response = await doHttp({
      method: 'get',
      url: id
        ? `/api/formatset_blueprint?formatset_id=${id}&filter=${type}`
        : `/api/formatset_blueprint?filter=${type}`,
    })

    const blueprint = decode(adapters.blueprint, response, 'default', {
      name: type + '-formatset',
    })

    return blueprint
  }

  const getCreativeGuidelines: GetCreativeGuidelines = async (id) => {
    try {
      const recommendationsResponse = await doHttp(
        {
          method: 'get',
          url: `/api/creatives/${id}/recommendations`,
        },
        { throwOnError: true },
      )
      const guidelines = decode(adapters.guidelines, recommendationsResponse)

      return resultSuccess(guidelines)
    } catch (e) {
      return resultError(appErrorFromAnything(e))
    }
  }

  return {
    initCreativeForm,
    initFormatsetForm,

    getCreativeGuidelines,
    getFormatsetBlueprint,
  }
}

export default blueprintService
